// libraries
import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import { actions as offerReduxActionTypes } from '@makemydeal/dr-offer-redux';

// action types
import { APPLY_LEASE_CONTRACT_FIELDS } from './actionTypes/leaseContractingFieldsActionTypes';

/**
 * Edits that re-price the deal against a *different* lender program, so the overrides describing the old one no
 * longer mean anything (F283308 AC5: overrides discarded, values reset to the new program's, indicators clear).
 *
 * Shared because two layers have to agree on this answer: `programInvalidationMiddleware` runs the fresh program
 * quote, and `manualFieldOverridesReducer` clears the override indicators. They were hand-synced lists until
 * DE766084, where both were missing Annual Mileage and both matched a zip action nothing dispatches.
 *
 * Only membership is shared. The middleware additionally requires `meta.middleware.payment`; the reducer must
 * not, because an edit dispatched with the payment call suppressed still invalidates the overrides on screen.
 *
 * AC5 also names Vehicle and re-checking Auto Select, and neither is here: each already has an owner that
 * switches the endpoint itself (`changeVehicleMiddleware`, the Auto Select checkbox), so a second mechanism on
 * the same action would double-switch. The checkbox clears the indicators directly; a vehicle change does not,
 * which is a gap in AC5's coverage rather than a decision this file records.
 */
export const PROGRAM_INVALIDATING_ACTIONS: ReadonlySet<string> = new Set([
    offerReduxActionTypes.SELECTED_TERM,
    // DE766084: reinstated. Reading it as "re-prices without changing which program applies" contradicted AC5
    // and left an indicator standing after a mileage change - do not drop it again without an AC change.
    offerReduxActionTypes.SELECTED_ANNUAL_MILES,
    offerReduxActionTypes.UPDATED_OFFER_TYPE,
    offerReduxActionTypes.UPDATED_PAYMENT_FREQUENCY
]);

/**
 * Just Annual Mileage, deliberately. `middleware/__common__/pqSupportedOverrides` owns the complementary set
 * of Mileage & Cost Options fields that only user-program-quotes can price - `standardAllowedMiles`,
 * `excessMileRate`, `thresholdMiles`, `inceptionRate`, `retroMiles`, `mileagePenaltyRate` - and routes them to
 * a UPQ fallback. Annual Mileage is the one program-quotes reads to pick the mileage tier's program, which is
 * why it invalidates and they do not. Adding one of theirs here would make this the winning verdict
 * (`isInvalidatingTrigger` is checked before `isOverrideTrigger`) and force a PQ refetch for a field PQ
 * cannot price.
 */
const MILEAGE_FIELDS: ReadonlySet<string> = new Set(['annualMiles']);

/** The mileage slide-out batches its apply, carrying the fields in meta rather than in the action type. */
export const isMileageFieldsApply = (action: AnyFSA): boolean => {
    if (action.type !== APPLY_LEASE_CONTRACT_FIELDS) return false;
    const changedFields = action.meta?.changedFields;
    return Array.isArray(changedFields) && changedFields.some((field: string) => MILEAGE_FIELDS.has(field));
};

/**
 * AC5's Customer Zip Code. `UPDATED_SHOPPER_ZIP` stood in for it and is unreachable from the UI, so this reads
 * the action the customer card dispatches - which also fires for a name or phone edit, hence the flag rather
 * than the bare type. `updatedShopperLocationFromConsumer` sets it only when the five-digit zip moved.
 */
export const isShopperZipChange = (action: AnyFSA): boolean =>
    action.type === offerReduxActionTypes.UPDATED_SHOPPER_CONTACT && action.meta?.shopperZipChanged === true;

export const KEEP_LENDER_META = 'keepLenderOnZipChange';

export type KeptLender = { lenderId: string | number; programName?: string; rateProgram?: string };

export const invalidatesProgram = (action: AnyFSA): boolean =>
    PROGRAM_INVALIDATING_ACTIONS.has(action.type) || isMileageFieldsApply(action) || isShopperZipChange(action);
